import { useState, useEffect } from "react";
import { Plus, Minus, History, AlertTriangle } from "lucide-react";
import axios from "axios";
import AdminLayout from "../../components/AdminLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import { API } from "../../App";
import { format } from "date-fns";
import { toast } from "sonner";
import { motion } from "framer-motion";

const AdminInventory = () => {
  const [inventory, setInventory] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [stockChange, setStockChange] = useState(0);
  const [reason, setReason] = useState("");
  const [showLogs, setShowLogs] = useState(false);
  const [updating, setUpdating] = useState(false);

  const fetchInventory = async () => {
    try {
      const response = await axios.get(`${API}/admin/inventory`, { withCredentials: true });
      setInventory(response.data);
    } catch (error) {
      console.error("Error fetching inventory:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLogs = async (productId = null) => {
    try {
      let url = `${API}/admin/inventory/logs`;
      if (productId) url += `?product_id=${productId}`;
      const response = await axios.get(url, { withCredentials: true });
      setLogs(response.data);
    } catch (error) {
      console.error("Error fetching logs:", error);
    }
  };

  useEffect(() => {
    fetchInventory();
    fetchLogs();
  }, []);

  const handleUpdateStock = async () => {
    if (!selectedProduct || stockChange === 0) return;
    
    setUpdating(true);
    try {
      await axios.put(`${API}/admin/inventory/${selectedProduct.product_id}`, {
        stock_change: stockChange,
        reason: reason || "Manual adjustment"
      }, { withCredentials: true });
      
      toast.success("Inventory updated successfully");
      setSelectedProduct(null);
      setStockChange(0);
      setReason("");
      fetchInventory();
      fetchLogs();
    } catch (error) {
      toast.error("Failed to update inventory");
    } finally {
      setUpdating(false);
    }
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'MMM dd, HH:mm');
    } catch {
      return dateString;
    }
  };

  return (
    <AdminLayout>
      <div data-testid="admin-inventory">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Inventory</h1>
            <p className="text-muted-foreground mt-1">Manage product stock levels</p>
          </div>
          
          <Button 
            variant="outline" 
            className="border-white/10"
            onClick={() => setShowLogs(!showLogs)}
            data-testid="toggle-logs"
          >
            <History className="w-4 h-4 mr-2" />
            {showLogs ? 'Hide Logs' : 'View Logs'}
          </Button>
        </div>

        {/* Low Stock Alert */}
        {inventory.some(p => p.is_low_stock) && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-xl flex items-center gap-3"
          >
            <AlertTriangle className="w-5 h-5 text-yellow-500" />
            <p className="text-sm text-foreground">
              Some products have low stock. Consider restocking soon.
            </p>
          </motion.div>
        )}

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Inventory Table */}
          <div className={`${showLogs ? 'lg:col-span-2' : 'lg:col-span-3'}`}>
            {loading ? (
              <div className="animate-pulse space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-20 bg-card rounded-xl"></div>
                ))}
              </div>
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="bg-card border border-white/5 rounded-xl overflow-hidden"
              >
                <div className="overflow-x-auto">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>SKU</th>
                        <th>Product</th>
                        <th>Weight</th>
                        <th>Price</th>
                        <th>Current Stock</th>
                        <th>Threshold</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {inventory.map((product) => (
                        <tr key={product.product_id} data-testid={`inventory-row-${product.sku}`}>
                          <td className="font-mono text-sm text-muted-foreground">{product.sku}</td>
                          <td className="font-medium text-foreground">{product.name}</td>
                          <td>{product.weight >= 1000 ? `${product.weight/1000}kg` : `${product.weight}g`}</td>
                          <td>₹{product.price}</td>
                          <td className={`font-bold ${product.is_low_stock ? 'text-red-500' : 'text-foreground'}`}>
                            {product.stock}
                          </td>
                          <td className="text-muted-foreground">{product.low_stock_threshold}</td>
                          <td>
                            {product.stock === 0 ? (
                              <span className="px-2 py-1 bg-red-500/10 text-red-500 text-xs rounded">Out of Stock</span>
                            ) : product.is_low_stock ? (
                              <span className="px-2 py-1 bg-yellow-500/10 text-yellow-500 text-xs rounded">Low Stock</span>
                            ) : (
                              <span className="px-2 py-1 bg-green-500/10 text-green-500 text-xs rounded">In Stock</span>
                            )}
                          </td>
                          <td>
                            <Dialog>
                              <DialogTrigger asChild>
                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  className="border-white/10"
                                  onClick={() => setSelectedProduct(product)}
                                  data-testid={`update-stock-${product.sku}`}
                                >
                                  Update
                                </Button>
                              </DialogTrigger>
                              <DialogContent className="bg-card border-white/10">
                                <DialogHeader>
                                  <DialogTitle>Update Stock - {product.name}</DialogTitle>
                                </DialogHeader>
                                <div className="space-y-4 pt-4">
                                  <div className="text-center">
                                    <p className="text-sm text-muted-foreground">Current Stock</p>
                                    <p className="text-3xl font-bold text-foreground">{product.stock}</p>
                                  </div>
                                  
                                  <div className="flex items-center justify-center gap-4">
                                    <Button 
                                      variant="outline" 
                                      size="icon"
                                      onClick={() => setStockChange(stockChange - 10)}
                                      className="border-white/10"
                                    >
                                      <Minus className="w-4 h-4" />
                                    </Button>
                                    <Input
                                      type="number"
                                      value={stockChange}
                                      onChange={(e) => setStockChange(parseInt(e.target.value) || 0)}
                                      className="w-24 text-center bg-background border-white/10"
                                      data-testid="stock-change-input"
                                    />
                                    <Button 
                                      variant="outline" 
                                      size="icon"
                                      onClick={() => setStockChange(stockChange + 10)}
                                      className="border-white/10"
                                    >
                                      <Plus className="w-4 h-4" />
                                    </Button>
                                  </div>
                                  
                                  <p className="text-center text-sm">
                                    New Stock: <span className="font-bold text-foreground">{Math.max(0, product.stock + stockChange)}</span>
                                  </p>
                                  
                                  <div className="space-y-2">
                                    <Label>Reason (optional)</Label>
                                    <Input
                                      value={reason}
                                      onChange={(e) => setReason(e.target.value)}
                                      placeholder="e.g., New shipment received"
                                      className="bg-background border-white/10"
                                      data-testid="reason-input"
                                    />
                                  </div>
                                  
                                  <Button 
                                    onClick={handleUpdateStock}
                                    disabled={stockChange === 0 || updating}
                                    className="w-full bg-primary hover:bg-primary/90"
                                    data-testid="confirm-update-btn"
                                  >
                                    {updating ? "Updating..." : "Update Stock"}
                                  </Button>
                                </div>
                              </DialogContent>
                            </Dialog>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </motion.div>
            )}
          </div>

          {/* Inventory Logs */}
          {showLogs && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="lg:col-span-1 bg-card border border-white/5 rounded-xl h-fit max-h-[600px] overflow-hidden"
              data-testid="inventory-logs"
            >
              <div className="p-4 border-b border-white/5">
                <h3 className="font-semibold text-foreground">Stock History</h3>
              </div>
              <div className="overflow-y-auto max-h-[500px]">
                {logs.length === 0 ? (
                  <div className="p-4 text-center text-muted-foreground text-sm">
                    No inventory changes yet
                  </div>
                ) : (
                  <div className="divide-y divide-white/5">
                    {logs.slice(0, 20).map((log) => (
                      <div key={log.log_id} className="p-4">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-mono text-xs text-muted-foreground">{log.sku}</span>
                          <span className={`text-sm font-bold ${log.quantity_change > 0 ? 'text-green-500' : 'text-red-500'}`}>
                            {log.quantity_change > 0 ? '+' : ''}{log.quantity_change}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground">{log.reason}</p>
                        <p className="text-xs text-muted-foreground mt-1">{formatDate(log.created_at)}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminInventory;
